import type { LngLat } from "./geo/types.ts";
import { isValidLngLat } from "./geo/validation.ts";

const IPINFO_URL = "https://ipinfo.io/json";
const IP_GEOLOCATION_ZOOM = {
  city: 10,
  country: 5,
} as const;

interface IpInfoResponse {
  loc?: string;
  city?: string;
}

export interface IpInfoLocation {
  lngLat: LngLat;
  zoom: number;
}

export function parseIpInfoLocation(
  value: unknown,
): IpInfoLocation | undefined {
  if (typeof value !== "object" || value === null) return undefined;

  const { loc, city } = value as IpInfoResponse;
  if (typeof loc !== "string") return undefined;

  const [latitudeText, longitudeText, ...extra] = loc.split(",");
  if (!latitudeText || !longitudeText || extra.length > 0) return undefined;

  const latitude = Number(latitudeText);
  const longitude = Number(longitudeText);
  const lngLat: LngLat = [longitude, latitude];
  if (!isValidLngLat(lngLat)) {
    return undefined;
  }

  return {
    lngLat,
    zoom: city?.trim() ? IP_GEOLOCATION_ZOOM.city : IP_GEOLOCATION_ZOOM.country,
  };
}

export async function fetchIpInfoLocation(
  token: string,
  signal?: AbortSignal,
  fetchImpl: typeof fetch = fetch,
): Promise<IpInfoLocation> {
  const response = await fetchImpl(IPINFO_URL, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  const responseBody = await response.text();
  if (!response.ok) {
    throw new Error(
      `IP geolocation request failed with ${response.status}: ${responseBody}`,
    );
  }

  let payload: IpInfoResponse;
  try {
    payload = JSON.parse(responseBody) as IpInfoResponse;
  } catch {
    throw new Error(
      `IP geolocation response was not valid JSON: ${responseBody}`,
    );
  }

  const location = parseIpInfoLocation(payload);
  if (!location) {
    throw new Error("IP geolocation response did not contain a valid location");
  }
  return location;
}
